import React from 'react';

import Button from 'jsx/elements/buttons/Button';
import Flex from 'jsx/layout/Flex';
import Spinner from 'jsx/elements/spinner';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import Warning from 'jsx/elements/messages/Warning';

import { api } from 'api';
import Modal from 'components/Modal';
import useModalMinHeightFix from 'hooks/useModalMinHeightFix';
import ExclusionTable from './ExclusionTable';
import ExclusionUpload from './ExclusionUpload';
import ExclusionRemoval from './ExclusionRemoval';

function ExclusionModal({ onClose }) {
  const { t } = useLocale();
  const [{ data, status, removing, removalSuccess, removeStatus }, dispatch] = React.useReducer(
    reducer,
    initialState
  );
  const modalRef = React.useRef(null);
  const minHeight = useModalMinHeightFix(modalRef, data);

  React.useEffect(() => load(), []);

  function load() {
    dispatch({ type: 'SET_LOADING' });
    api.paymentBehavior.loadExclusionList().then((res) => {
      if (!res.error) {
        dispatch({ type: 'SET_DATA', payload: res });
      }
    });
  }

  function onRemove(customer) {
    dispatch({ type: 'SHOW_REMOVE', payload: customer });
  }

  function clearRemove() {
    dispatch({ type: 'CLEAR_REMOVE' });
  }

  async function doRemove() {
    dispatch({ type: 'START_REMOVING' });
    const res = await api.paymentBehavior.removeExclusion(removing.payorRegno);
    if (res === null) {
      dispatch({ type: 'REMOVE_REGNO' });
    }
  }

  const Actions = () => {
    if (removing && !removalSuccess) {
      return (
        <Flex justifyContent="flex-end" style={{ width: '100%' }}>
          <div style={{ marginRight: 10 }}>
            <Button
              disabled={removeStatus === 'saving'}
              text={t('button_no_cancel')}
              color="clear"
              onClick={clearRemove}
            />
          </div>
          <Button
            disabled={removeStatus === 'saving'}
            text={t('button_yes_remove')}
            onClick={doRemove}
          />
        </Flex>
      );
    } else {
      return (
        <Flex justifyContent="flex-end" style={{ width: '100%' }}>
          <Button text={t('button_yes_close')} onClick={onClose} />
        </Flex>
      );
    }
  };

  return (
    <Modal header={t('exclusionModal_header')} isOpen footer={<Actions />}>
      <div ref={modalRef} style={{ minHeight }}>
        <ExclusionUpload isOpen t={t} update={load} />
        {status !== 'loaded' && data.length === 0 && <Spinner size="medium" relCenter />}
        {status === 'loaded' && data.length === 0 && (
          <Warning messageHeader={t('portfolio_exclusion_info_empty_list')} messageType="info" />
        )}
        {removing !== null && (
          <ExclusionRemoval
            data={removing}
            showSuccess={removalSuccess}
            closeSuccess={clearRemove}
          />
        )}
        {(removing === null || removalSuccess) && data.length > 0 && (
          <ExclusionTable data={data} onRemove={onRemove} />
        )}
      </div>
    </Modal>
  );
}

export default ExclusionModal;

const initialState = {
  data: [],
  status: 'loading',
  removing: null,
  removalSuccess: false,
  removeStatus: 'initial',
};

function reducer(state, { type, payload }) {
  switch (type) {
    case 'START_REMOVING':
      return { ...state, removeStatus: 'saving' };
    case 'SET_LOADING':
      return { ...state, status: 'loading' };
    case 'SET_DATA':
      return { ...state, status: 'loaded', data: payload };
    case 'REMOVE_REGNO':
      return {
        ...state,
        data: [...state.data.filter((obj) => obj.payorRegno !== state.removing.payorRegno)],
        removalSuccess: true,
        removeStatus: 'initial',
      };
    case 'SHOW_REMOVE':
      return { ...state, removing: payload, removalSuccess: false };
    case 'CLEAR_REMOVE':
      return { ...state, removing: null, removalSuccess: false };
    default:
      return state;
  }
}
